import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, Router } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { combineLatest, filter, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TReducerState } from '../../../app.config';
import { slideshowContentActions } from '../../../state/slideshow-content/slideshow-content.actions';
import { ELoadStatus } from '../../../state/state.interface';
import { userActions } from '../../../state/user/user.actions';
import { UrlParseUtilitiesService } from '../../../utilities/url-parse-utilities.service';

@Injectable()
export class SlideshowContentResolver implements Resolve<Observable<unknown> | Promise<boolean>> {
  private readonly isDataLoaded$ = this.store
    .select(({ slideshowContent }) => slideshowContent.slideshowDataLoadStatus)
    .pipe(filter((status) => status === ELoadStatus.success));
  private readonly isCurrentUserUpdateDone$ = this.store
    .select(({ user }) => user.getCurrentUserStatus)
    .pipe(filter((status) => status === ELoadStatus.success));

  constructor(
    private readonly store: Store<TReducerState>,
    private readonly router: Router,
    private readonly urlParseUtilitiesService: UrlParseUtilitiesService,
    actionsSubject: ActionsSubject,
  ) {
    actionsSubject
      .pipe(
        ofType(slideshowContentActions.fetchError.type),
        filter(({ exceptionSource }) => exceptionSource === 'fetchSlideshowContents'),
        takeUntil(this.isDataLoaded$),
      )
      .subscribe(() => {
        this.navigateToHome();
      });
  }

  public resolve(route: ActivatedRouteSnapshot): Observable<unknown> | Promise<boolean> {
    const slideshowId = this.urlParseUtilitiesService.tryParseJson<number | null>(route.paramMap.get('id'), null);

    if (
      !slideshowId ||
      typeof slideshowId !== 'number' ||
      Number.isNaN(slideshowId) ||
      !Number.isInteger(slideshowId)
    ) {
      return this.navigateToHome();
    }

    this.store.dispatch(slideshowContentActions.fetchSlideshowContents({ slideshowId }));
    this.store.dispatch(userActions.getCurrentUser());

    return combineLatest([this.isDataLoaded$, this.isCurrentUserUpdateDone$]);
  }

  private navigateToHome(): Promise<boolean> {
    return this.router.navigate(['/slideshows']);
  }
}
